/**
 * Utility functions for displaying user information with privacy logic.
 * Handles anonymous_id vs real name visibility based on viewer permissions.
 */

import { User } from '../types';

type UserLike = {
  first_name?: string;
  last_name?: string;
  anonymous_id?: string;
  is_premium?: boolean;
  id?: number | string;
  role?: string;
};

/**
 * Get the display name for a user based on viewer's permissions.
 * 
 * @param user - The user whose name to display
 * @param currentUser - The current logged-in user (viewer)
 * @returns Display name (real name or anonymous ID)
 */
export function getUserDisplayName(
  user: User | UserLike | null | undefined,
  currentUser?: User | UserLike | null
): string {
  if (!user) return 'Utente sconosciuto';

  // If viewer is the same user, show real name
  if (currentUser && user.id && currentUser.id && String(currentUser.id) === String(user.id)) {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
  }

  // If viewer is premium or admin, show real name
  if (currentUser && (currentUser.is_premium || currentUser.role === 'admin')) {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
  }

  // Otherwise show anonymous ID
  return user.anonymous_id || 'GR-[PENDING]';
}

/**
 * Get initials from user name or anonymous ID.
 * 
 * @param user - The user
 * @param currentUser - The current logged-in user (viewer)
 * @returns User initials (2 letters)
 */
export function getUserInitials(
  user: User | UserLike | null | undefined,
  currentUser?: User | UserLike | null
): string {
  if (!user) return 'GU';

  // If viewer can see real name, use initials from name
  const canSeeRealName = (
    (currentUser && user.id && currentUser.id && String(currentUser.id) === String(user.id)) ||
    (currentUser && (currentUser.is_premium || currentUser.role === 'admin'))
  );

  if (canSeeRealName && user.first_name && user.last_name) {
    return `${user.first_name.charAt(0).toUpperCase()}${user.last_name.charAt(0).toUpperCase()}`;
  }

  // Otherwise use first 2 chars from anonymous ID (skip "GR-")
  if (user.anonymous_id && user.anonymous_id.length >= 5) {
    const idPart = user.anonymous_id.substring(3).replace(/\s/g, ''); // Remove "GR-" and spaces
    return idPart.substring(0, 2).toUpperCase();
  }

  return 'GU';
}

/**
 * Check if user is premium.
 * 
 * @param user - The user to check
 * @returns true if user is premium
 */
export function isUserPremium(user: User | UserLike | null | undefined): boolean {
  return user?.is_premium === true;
}

/**
 * Get premium badge emoji/icon.
 * 
 * @param user - The user to check
 * @returns Premium badge string or empty string
 */
export function getPremiumBadge(user: User | UserLike | null | undefined): string {
  return isUserPremium(user) ? '⭐' : '';
}
